import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, CheckCheck } from "lucide-react";
import { api } from "../../lib/api";
import { useI18n } from "../../context/I18nContext";

const timeAgo = (iso) => {
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "ahora";
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  return `${Math.floor(s / 86400)}d`;
};

export default function NotificationBell() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [count, setCount] = useState(0);
  const ref = useRef(null);

  const loadCount = useCallback(() => {
    api.get("/notifications/unread-count").then((r) => setCount(r.data.count)).catch(() => {});
  }, []);
  const loadItems = useCallback(() => {
    api.get("/notifications").then((r) => setItems(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    loadCount();
    loadItems();
    const iv = setInterval(loadCount, 30000);
    return () => clearInterval(iv);
  }, [loadCount, loadItems]);

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next) loadItems();
  };

  const openItem = async (n) => {
    if (!n.read) { await api.post(`/notifications/${n.id}/read`).catch(() => {}); }
    setOpen(false); loadCount();
    if (n.link) navigate(n.link);
  };

  const markAll = async () => {
    await api.post("/notifications/read-all").catch(() => {});
    loadCount(); loadItems();
  };

  return (
    <div className="relative" ref={ref}>
      <button data-testid="notif-bell" onClick={toggle}
        className="relative w-9 h-9 rounded-full border border-navy/15 flex items-center justify-center text-navy/60 hover:text-navy hover:border-navy/30 transition-colors">
        <Bell className="w-4 h-4" />
        {count > 0 && (
          <span data-testid="notif-badge" className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-violet text-white text-[10px] flex items-center justify-center">
            {count > 9 ? "9+" : count}
          </span>
        )}
      </button>
      {open && (
        <div data-testid="notif-dropdown" className="absolute right-0 mt-2 w-80 max-h-[70vh] overflow-y-auto bg-white border border-navy/10 rounded-xl shadow-xl shadow-navy/10 z-50">
          <div className="flex items-center justify-between px-4 py-3 border-b border-navy/10 sticky top-0 bg-white">
            <span className="font-medium text-sm">{t("os.notif.title")}</span>
            <button onClick={markAll} className="text-xs text-violet hover:underline flex items-center gap-1"><CheckCheck className="w-3.5 h-3.5" /> {t("os.notif.markAll")}</button>
          </div>
          {items.length === 0 ? (
            <div className="px-4 py-10 text-center text-sm text-navy/40" data-testid="notif-empty">{t("os.notif.empty")}</div>
          ) : (
            items.map((n) => (
              <button key={n.id} data-testid={`notif-item-${n.id}`} onClick={() => openItem(n)}
                className={`w-full text-left px-4 py-3 border-b border-navy/5 last:border-0 hover:bg-cream/60 transition-colors ${n.read ? "" : "bg-violet/5"}`}>
                <div className="flex items-start gap-2">
                  {!n.read && <span className="mt-1.5 w-2 h-2 rounded-full bg-violet shrink-0" />}
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium truncate">{n.title}</div>
                    <div className="text-xs text-navy/55 line-clamp-2">{n.body}</div>
                    <div className="text-[10px] text-navy/35 mt-1">{timeAgo(n.created_at)}</div>
                  </div>
                </div>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
